import React, { useState, useEffect, Suspense, lazy } from "react";
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import Box from "@mui/material/Box";
const Login = lazy(() => import("./pages/Login"));
const Landing = lazy(() => import("./pages/Landing"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const ResellerDashboard = lazy(() => import("./pages/ResellerDashboard"));
const UserDashboard = lazy(() => import("./pages/UserDashboard"));
const AgencyDashboard = lazy(() => import("./pages/AgencyDashboard"));
const StaffDashboard = lazy(() => import("./pages/StaffDashboard"));
const FreelancerDashboard = lazy(() => import("./pages/FreelancerDashboard"));
const Register = lazy(() => import("./pages/Register"));
const PromoRedirect = lazy(() => import("./pages/PromoRedirect"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Contact = lazy(() => import("./pages/Contact"));
const PublicFeeds = lazy(() => import("./pages/PublicFeeds"));
const PrompterPublicView = lazy(() => import("./pages/PrompterPublicView"));
const PublicIntercomView = lazy(() => import("./pages/PublicIntercomView"));
const PublicRos = lazy(() => import("./pages/PublicRos"));
const Countdown = lazy(() => import("./pages/Countdown"));
const ClockView = lazy(() => import("./pages/ClockView"));
const ErrorPage = lazy(() => import("./pages/ErrorPage"));
const PublicCrewProfile = lazy(
  () => import("./pages/PublicCrewProfileExperience"),
);
const PublicUserProfile = lazy(() => import("./pages/PublicUserProfile"));
const PublicJobs = lazy(() => import("./pages/PublicJobs"));
const PublicJobDetail = lazy(() =>
  import("./pages/PublicJobs").then((module) => ({
    default: module.PublicJobDetail,
  })),
);
const PublicFreelancerAds = lazy(() =>
  import("./pages/PublicJobs").then((module) => ({
    default: module.PublicFreelancerAds,
  })),
);
const PublicFreelancerAdDetail = lazy(() =>
  import("./pages/PublicJobs").then((module) => ({
    default: module.PublicFreelancerAdDetail,
  })),
);
const PublicPlayerView = lazy(() => import("./pages/PublicPlayerView"));
const PublicAgencyEventPage = lazy(() =>
  import("./pages/AgencyTicketingPublic").then((module) => ({
    default: module.PublicAgencyEventPage,
  })),
);
const PublicAgencyTicketPage = lazy(() =>
  import("./pages/AgencyTicketingPublic").then((module) => ({
    default: module.PublicAgencyTicketPage,
  })),
);
const PublicAgencyClaimPage = lazy(() =>
  import("./pages/AgencyTicketingPublic").then((module) => ({
    default: module.PublicAgencyClaimPage,
  })),
);
const PublicAgencyTicketScanner = lazy(() =>
  import("./pages/AgencyTicketingPublic").then((module) => ({
    default: module.PublicAgencyTicketScanner,
  })),
);
const AgencyTicketingLanding = lazy(
  () => import("./pages/AgencyTicketingLanding"),
);
const Integrations = lazy(() => import("./pages/Integrations"));
const IntegrationDetail = lazy(() => import("./pages/IntegrationDetail"));
const Search = lazy(() => import("./pages/Search"));
const OAuthCallback = lazy(() => import("./pages/OAuthCallback"));
const Services = lazy(() => import("./pages/Services"));
const Service = lazy(() => import("./pages/Service"));
const IntentLanding = lazy(() => import("./pages/IntentLanding"));
const CompareServers = lazy(() => import("./pages/CompareServers"));
const BroadcastToolkit = lazy(() => import("./pages/BroadcastToolkit"));
const UserAtemControlView = lazy(() => import("./pages/UserAtemControlView"));
// CTA landing pages
const FreelancerBasicCTA = lazy(() => import("./cta/FreelancerBasicCTA"));
const FreelancerProCTA = lazy(() => import("./cta/FreelancerProCTA"));
const FreelancerInterviewPrepCTA = lazy(
  () => import("./cta/FreelancerInterviewPrepCTA"),
);
const FreelancerPortfolioCTA = lazy(
  () => import("./cta/FreelancerPortfolioCTA"),
);
const FreelancerLocalGigsCTA = lazy(
  () => import("./cta/FreelancerLocalGigsCTA"),
);
const FreelancerHighDemandCTA = lazy(
  () => import("./cta/FreelancerHighDemandCTA"),
);
const BroadcasterBookDemoCTA = lazy(
  () => import("./cta/BroadcasterBookDemoCTA"),
);
const BroadcasterSRTCloudCTA = lazy(
  () => import("./cta/BroadcasterSRTCloudCTA"),
);
const BroadcasterSolutionsCTA = lazy(
  () => import("./cta/BroadcasterSolutionsCTA"),
);
const BroadcasterEnterpriseCTA = lazy(
  () => import("./cta/BroadcasterEnterpriseCTA"),
);
import CookieConsent from "./components/CookieConsent";
import SuspenseLoader from "./components/SuspenseLoader";
import LanguageSwitcher from "./components/LanguageSwitcher";
import i18n from "./i18n";
import axios from "./api";
// import MaintenanceOverlay from './components/MaintenanceOverlay'
import ErrorOverlay from "./components/ErrorOverlay";
import ScrollToTop from "./components/ScrollToTop";
import { trackPageView } from "./utils/analytics";
import { isProbablyJwt } from "./utils/jwt";
import { setLocalizedMeta } from "./utils/head";
// import { PageLoadProvider } from './components/PageLoadContext'
function App() {
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [showLangOverlay, setShowLangOverlay] = useState(false);
  const [prerenderReady, setPrerenderReady] = useState(false);
  const prerenderTimerRef = React.useRef(null);
  // Theme handled by ThemeModeProvider
  useEffect(() => {
    if (!window.opener || window.opener === window) return;

    const hashParams = new URLSearchParams(window.location.hash.slice(1));
    const queryParams = new URLSearchParams(window.location.search);
    const accessToken = hashParams.get("access_token");
    const error = queryParams.get("error") || hashParams.get("error");
    const errorDescription =
      queryParams.get("error_description") ||
      hashParams.get("error_description");

    if (!accessToken && !error) return;

    window.opener.postMessage(
      {
        type: "facebook-login-callback",
        accessToken,
        error,
        errorDescription,
      },
      window.location.origin,
    );
    window.close();
  }, []);

  useEffect(() => {
    // Show language overlay for anonymous users regardless of cookie acceptance.
    try {
      setShowLangOverlay(true);
    } catch (e) {
      setShowLangOverlay(true);
    }
    const onStorage = (ev) => {
      if (ev.key === "cookie_accepted") {
        // Keep overlay visible for public users even if cookies were declined.
        try {
          setShowLangOverlay(true);
        } catch (e) {
          setShowLangOverlay(true);
        }
      }
    };
    window.addEventListener("storage", onStorage);
    const onCookieAccepted = (ev) => {
      // Keep overlay visible for public users even if cookies were declined.
      try {
        setShowLangOverlay(true);
      } catch (e) {
        setShowLangOverlay(true);
      }
    };
    window.addEventListener("cookie-accepted", onCookieAccepted);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("cookie-accepted", onCookieAccepted);
    };
  }, []);

  useEffect(() => {
    const onAuthExpired = () => {
      setUser(null);
      setAuthChecked(true);

      const currentPath = `${window.location.pathname}${window.location.search || ""}${window.location.hash || ""}`;
      if (window.location.pathname === "/login") return;
      const next = encodeURIComponent(currentPath || "/");
      navigate(`/login?next=${next}`, { replace: true });
    };

    window.addEventListener("auth:expired", onAuthExpired);
    return () => window.removeEventListener("auth:expired", onAuthExpired);
  }, [navigate]);

  // no automatic redirect on app start - maintenance page is shown only when
  // a network request fails while user interacts with the app
  useEffect(() => {
    // a network request fails while user interacts with the app
    let mounted = true;
    const token =
      localStorage.getItem("token") || sessionStorage.getItem("token");
    if (token && !isProbablyJwt(token)) {
      localStorage.removeItem("token");
      sessionStorage.removeItem("token");
      setAuthChecked(true);
      return;
    }
    if (token) {
      const tokenAtRequestStart = token;
      axios
        .get("/users/me", { skipHttpErrorEvent: true, skipErrorRedirect: true })
        .then(async (r) => {
          // if no tenant_id, might be freelancer; fetch crew profile to decide
          let crew = null;
          if (!r.data.tenant_id) {
            try {
              const c = await axios.get("/me/crew");
              crew = c.data;
            } catch {}
          }
          const u = { ...r.data, crew };
          if (!mounted) return;
          setUser(u);
          // Apply user's preferred_locale from backend, if present
          try {
            if (u?.preferred_locale && i18n.language !== u.preferred_locale) {
              await i18n.changeLanguage(u.preferred_locale);
              try {
                localStorage.setItem("app.lang", u.preferred_locale);
              } catch (e) {}
            }
          } catch (e) {
            /* ignore language change errors */
          }
          if (mounted) setAuthChecked(true);
        })
        .catch((error) => {
          if (!mounted) return;
          const currentToken =
            localStorage.getItem("token") || sessionStorage.getItem("token");
          if (
            error?.response?.status === 401 &&
            currentToken === tokenAtRequestStart
          ) {
            localStorage.removeItem("token");
            sessionStorage.removeItem("token");
          }
          setAuthChecked(true);
        });
    } else {
      setAuthChecked(true);
    }
    return () => {
      mounted = false;
    };
  }, []);
  useEffect(() => {
    trackPageView(
      `${location.pathname}${location.search}${location.hash}`,
      document.title,
    );
  }, [location.pathname, location.search, location.hash]);

  useEffect(() => {
    setLocalizedMeta(i18n.language);
  }, [location.pathname, location.search, location.hash, i18n.language]);

  useEffect(() => {
    if (!authChecked) return;
    setPrerenderReady(false);
    if (prerenderTimerRef.current) {
      clearTimeout(prerenderTimerRef.current);
    }
    prerenderTimerRef.current = setTimeout(() => {
      setPrerenderReady(true);
    }, 1200);
    return () => {
      if (prerenderTimerRef.current) {
        clearTimeout(prerenderTimerRef.current);
      }
    };
  }, [authChecked, location.pathname]);

  useEffect(() => {
    try {
      if (!(typeof window !== "undefined" && window.__PRERENDER_INJECTED))
        return;
      let done = false;
      const start = Date.now();
      const maxWait = 15000;
      const tick = () => {
        if (done) return;
        const hasLoader =
          typeof document !== "undefined" &&
          document.querySelector(".suspense-dot");
        if (!hasLoader || Date.now() - start > maxWait) {
          done = true;
          try {
            document.dispatchEvent(new Event("prerender-ready"));
          } catch (e) {
            /* ignore */
          }
          return;
        }
      };
      const intervalId = setInterval(tick, 200);
      const timeoutId = setTimeout(tick, maxWait + 500);
      return () => {
        done = true;
        clearInterval(intervalId);
        clearTimeout(timeoutId);
      };
    } catch (e) {
      /* ignore */
    }
  }, [location.pathname]);

  // If tokens are gone (logout), ensure user state is cleared so '/' renders Landing
  useEffect(() => {
    const token =
      localStorage.getItem("token") || sessionStorage.getItem("token");
    if (!token && user) {
      setUser(null);
    }
  }, [location.pathname, user]);

  const isAdminUser = !!(user && (user.is_admin || user.is_finance));
  const isResellerUser = !!(user && user.is_resaller && !isAdminUser);
  const publicRouteMatchers = [
    /^\/$/,
    /^\/services(\/|$)/,
    /^\/(transmisje-live|serwer-srt|restreaming|grafika-ekranowa|sufler-online|interkom-online|nagrywanie-transmisji-w-chmurze|scenariusz-transmisji|pay-per-view-transmisje|produkcja-zdalna|praca-dla-freelancerow|gielda-zlecen)(\/|$)/,
    /^\/compare$/,
    /^\/tools$/,
    /^\/prices$/,
    /^\/integrations(\/|$)/,
    /^\/faq$/,
    /^\/privacy$/,
    /^\/terms$/,
    /^\/contact$/,
    /^\/feeds$/,
    /^\/register$/,
    /^\/search$/,
    /^\/cta(\/|$)/,
    /^\/prompter(\/|$)/,
    /^\/intercom(\/|$)/,
    /^\/ros(\/|$)/,
    /^\/countdown$/,
    /^\/clock$/,
    /^\/crew(\/|$)/,
    /^\/user(\/|$)/,
    /^\/player(\/|$)/,
    /^\/promo(\/|$)/,
    /^\/ticketing(\/|$)/,
  ];
  const isPublicRoute = publicRouteMatchers.some((re) =>
    re.test(location.pathname),
  );

  if (!authChecked) return <div></div>;

  const handleOnLogin = async (u) => {
    setUser(u);
    if (u?.preferred_locale) {
      try {
        if (i18n.language !== u.preferred_locale) {
          await i18n.changeLanguage(u.preferred_locale);
          try {
            localStorage.setItem("app.lang", u.preferred_locale);
          } catch (e) {}
        }
      } catch (e) {
        /* ignore language change errors */
      }
    }
  };
  return (
    // <PageLoadProvider>
    <>
      <Suspense fallback={<SuspenseLoader />}>
        {prerenderReady && (
          <span data-prerender-ready style={{ display: "none" }} />
        )}
        {/* Don't show cookie consent overlay on marketing CTA or clock pages */}
        {!/^\/(cta|clock|player)(\/|$)/.test(location.pathname) && (
          <CookieConsent />
        )}
        {showLangOverlay &&
          !user &&
          !/^\/staff(\/|$)/.test(location.pathname) &&
          !/^\/prompter(\/|$)/.test(location.pathname) &&
          !/^\/clock(\/|$)/.test(location.pathname) &&
          !/^\/player(\/|$)/.test(location.pathname) &&
          !/^\/ticketing(\/|$)/.test(location.pathname) && (
            <Box
              sx={{
                position: "fixed",
                top: 8,
                right: 8,
                zIndex: 1501,
                pointerEvents: "auto",
              }}
            >
              <LanguageSwitcher size="small" compact persistBackend={false} />
            </Box>
          )}
        {/* <MaintenanceOverlay /> */}
        <ErrorOverlay />
        <ScrollToTop />
        <Routes>
          <Route
            path="/login"
            element={<Login onLogin={(u) => handleOnLogin(u)} />}
          />
          <Route
            path="/"
            element={
              user ? (
                isAdminUser ? (
                  <AdminDashboard />
                ) : isResellerUser ? (
                  <ResellerDashboard />
                ) : user.is_staff ? (
                  <StaffDashboard />
                ) : user.tenant_id ? (
                  <UserDashboard currentUser={user} />
                ) : user.agency_id ? (
                  <AgencyDashboard />
                ) : (
                  <FreelancerDashboard currentUser={user} />
                )
              ) : (
                <Landing />
              )
            }
          />
          <Route path="/terms" element={<Terms />} />
          <Route
            path="/staff"
            element={
              user ? <StaffDashboard /> : <Navigate to="/login" replace />
            }
          />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/feeds" element={<PublicFeeds />} />
          <Route path="/integrations" element={<Integrations />} />
          <Route path="/integrations/:slug" element={<IntegrationDetail />} />
          <Route path="/register" element={<Register />} />
          <Route
            path="/freelancer-register"
            element={<Navigate to="/register?type=freelancer" replace />}
          />
          <Route
            path="/broadcaster-register"
            element={<Navigate to="/register?type=broadcaster" replace />}
          />
          <Route
            path="/agency-register"
            element={<Navigate to="/register?type=agency" replace />}
          />
          <Route path="/promo/:ref" element={<PromoRedirect />} />
          <Route
            path="/prompter/:tenantId/:prompterId"
            element={<PrompterPublicView />}
          />
          <Route path="/intercom" element={<PublicIntercomView />} />
          <Route
            path="/ros/:tenantId/:rundownId/:key"
            element={<PublicRos />}
          />
          <Route path="/countdown" element={<Countdown />} />
          <Route path="/clock" element={<ClockView />} />
          <Route path="/crew/:slug" element={<PublicCrewProfile />} />
          <Route path="/user/:slug" element={<PublicUserProfile />} />
          <Route path="/jobs" element={<PublicJobs />} />
          <Route path="/jobs/:slug" element={<PublicJobDetail />} />
          <Route path="/freelancer-jobs" element={<PublicFreelancerAds />} />
          <Route
            path="/freelancer-jobs/:slug"
            element={<PublicFreelancerAdDetail />}
          />
          <Route path="/player/:publicId" element={<PublicPlayerView />} />
          <Route
            path="/ticketing"
            element={<AgencyTicketingLanding variant="service" />}
          />
          <Route
            path="/ticketing/events"
            element={<AgencyTicketingLanding variant="events" />}
          />
          <Route
            path="/ticketing/events/:slug"
            element={<PublicAgencyEventPage />}
          />
          <Route
            path="/ticketing/tickets/:token"
            element={<PublicAgencyTicketPage />}
          />
          <Route
            path="/ticketing/claims/:token"
            element={<PublicAgencyClaimPage />}
          />
          <Route
            path="/ticketing/scanner"
            element={<PublicAgencyTicketScanner />}
          />
          <Route
            path="/ticketing/scanner/:scannerKey"
            element={<PublicAgencyTicketScanner />}
          />
          <Route path="/search" element={<Search />} />
          <Route path="/compare" element={<CompareServers />} />
          <Route path="/tools" element={<BroadcastToolkit />} />
          <Route path="/prices" element={<Navigate to="/compare" replace />} />
          <Route path="/services" element={<Services />} />
          <Route path="/services/:service" element={<Service />} />
          <Route
            path="/transmisje-live"
            element={<IntentLanding intentKey="transmisje-live" />}
          />
          <Route
            path="/serwer-srt"
            element={<IntentLanding intentKey="serwer-srt" />}
          />
          <Route
            path="/restreaming"
            element={<IntentLanding intentKey="restreaming" />}
          />
          <Route
            path="/grafika-ekranowa"
            element={<IntentLanding intentKey="grafika-ekranowa" />}
          />
          <Route
            path="/sufler-online"
            element={<IntentLanding intentKey="sufler-online" />}
          />
          <Route
            path="/interkom-online"
            element={<IntentLanding intentKey="interkom-online" />}
          />
          <Route
            path="/nagrywanie-transmisji-w-chmurze"
            element={
              <IntentLanding intentKey="nagrywanie-transmisji-w-chmurze" />
            }
          />
          <Route
            path="/scenariusz-transmisji"
            element={<IntentLanding intentKey="scenariusz-transmisji" />}
          />
          <Route
            path="/pay-per-view-transmisje"
            element={<IntentLanding intentKey="pay-per-view-transmisje" />}
          />
          <Route
            path="/produkcja-zdalna"
            element={<IntentLanding intentKey="produkcja-zdalna" />}
          />
          <Route
            path="/praca-dla-freelancerow"
            element={<IntentLanding intentKey="praca-dla-freelancerow" />}
          />
          <Route
            path="/gielda-zlecen"
            element={<IntentLanding intentKey="gielda-zlecen" />}
          />
          <Route path="/error/:code" element={<ErrorPage />} />
          <Route path="/oauth-callback" element={<OAuthCallback />} />
          <Route
            path="/atem-control"
            element={
              user ? <UserAtemControlView /> : <Navigate to="/login" replace />
            }
          />
          {/* CTA landing pages (public shareable CTAs) */}
          <Route
            path="/cta/freelancer/basic"
            element={<FreelancerBasicCTA />}
          />
          <Route path="/cta/freelancer/pro" element={<FreelancerProCTA />} />
          <Route
            path="/cta/freelancer/interview"
            element={<FreelancerInterviewPrepCTA />}
          />
          <Route
            path="/cta/freelancer/portfolio"
            element={<FreelancerPortfolioCTA />}
          />
          <Route
            path="/cta/freelancer/local-gigs"
            element={<FreelancerLocalGigsCTA />}
          />
          <Route
            path="/cta/freelancer/high-demand"
            element={<FreelancerHighDemandCTA />}
          />
          <Route
            path="/cta/broadcaster/book-demo"
            element={<BroadcasterBookDemoCTA />}
          />
          <Route
            path="/cta/broadcaster/srt-cloud"
            element={<BroadcasterSRTCloudCTA />}
          />
          <Route
            path="/cta/broadcaster/solutions"
            element={<BroadcasterSolutionsCTA />}
          />
          <Route
            path="/cta/broadcaster/enterprise"
            element={<BroadcasterEnterpriseCTA />}
          />
          <Route path="*" element={<ErrorPage />} />
          <Route
            path="/app"
            element={
              user ? (
                isAdminUser ? (
                  <AdminDashboard />
                ) : isResellerUser ? (
                  <ResellerDashboard />
                ) : user.is_staff ? (
                  <StaffDashboard />
                ) : user.tenant_id ? (
                  <UserDashboard currentUser={user} />
                ) : user.agency_id ? (
                  <AgencyDashboard />
                ) : (
                  <FreelancerDashboard currentUser={user} />
                )
              ) : (
                <Navigate to="/login" />
              )
            }
          />
        </Routes>
      </Suspense>
    </>
    // </PageLoadProvider>
  );
}
export default App;
